/**
 * Platform detection utilities for Capacitor mobile app.
 *
 * In remote mode, the Capacitor WebView loads our Vercel URL.
 * These helpers let components adapt behavior for native vs web
 * (e.g., skip cookie banners, use native share sheet).
 *
 * Detection strategy (ordered by reliability):
 * 1. window.Capacitor bridge (works when Capacitor JS runtime is loaded)
 * 2. iOS standalone WebView detection (navigator.standalone)
 * 3. Android WebView user-agent flag ("wv")
 */

type Platform = 'ios' | 'android' | 'web'

/** Cache the result to avoid repeated detection */
let _cachedIsNative: boolean | null = null
let _cachedPlatform: Platform | null = null

/**
 * Check if running inside a Capacitor native WebView.
 * Safe to call on both server and client — returns false on server.
 *
 * Uses multiple detection strategies because in Capacitor remote mode
 * (server.url pointing to a web app), the Capacitor JS bridge may not
 * be fully injected by the time React components mount.
 */
export function isNativePlatform(): boolean {
  if (typeof window === 'undefined') return false
  if (_cachedIsNative !== null) return _cachedIsNative

  try {
    // Strategy 1: Capacitor bridge (most reliable when available)
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { isNativePlatform?: () => boolean }
      | undefined
    if (cap?.isNativePlatform?.()) {
      _cachedIsNative = true
      return true
    }

    // Strategy 2: iOS WebView detection
    // In a WKWebView (Capacitor iOS), navigator.standalone is a boolean (true or false).
    // In regular mobile Safari, it's only true when launched from home screen as PWA.
    // We check for standalone AND no browser chrome (no history.length reset patterns).
    const nav = navigator as { standalone?: boolean }
    if (nav.standalone === true) {
      _cachedIsNative = true
      return true
    }

    // Strategy 3: Android WebView detection
    // Android WebView adds "; wv)" to the user agent string
    const ua = navigator.userAgent
    if (/; wv\)/.test(ua) && /Android/.test(ua)) {
      _cachedIsNative = true
      return true
    }

    _cachedIsNative = false
    return false
  } catch {
    _cachedIsNative = false
    return false
  }
}

/**
 * Get the current platform: 'ios', 'android', or 'web'.
 * Safe to call on server — returns 'web'.
 */
export function getPlatform(): Platform {
  if (typeof window === 'undefined') return 'web'
  if (_cachedPlatform !== null) return _cachedPlatform

  try {
    // Try Capacitor bridge first
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { getPlatform?: () => string }
      | undefined
    const platform = cap?.getPlatform?.()
    if (platform === 'ios' || platform === 'android') {
      _cachedPlatform = platform
      return platform
    }

    // Fallback: detect from user agent when in native platform
    if (isNativePlatform()) {
      const ua = navigator.userAgent
      if (/iPhone|iPad|iPod/.test(ua)) {
        _cachedPlatform = 'ios'
        return 'ios'
      }
      if (/Android/.test(ua)) {
        _cachedPlatform = 'android'
        return 'android'
      }
    }

    _cachedPlatform = 'web'
    return 'web'
  } catch {
    _cachedPlatform = 'web'
    return 'web'
  }
}

/** Convenience: true when running on iOS */
export function isIOS(): boolean {
  return getPlatform() === 'ios'
}

/** Convenience: true when running on Android */
export function isAndroid(): boolean {
  return getPlatform() === 'android'
}

/**
 * Reset cached values. Useful for testing or when
 * Capacitor bridge becomes available after initial load.
 */
export function resetPlatformCache(): void {
  _cachedIsNative = null
  _cachedPlatform = null
}
